import React, { useState, useEffect, useCallback, useRef } from 'react';
import wordList from 'content/en/enable1_words.json';
import CollapsibleWordList from '../common/CollapsibleWordList';
import GameOver from './GameOver';
import { GameScore } from 'services/gameService';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import IconButton from '../buttons/IconButton';

import './WordWorm.css';
import './GameModal.css';

const beepSound = require('assets/sounds/beep-1.mp3');

type WordListType = { [key: string]: number };

interface WordWormGameModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onHighScore?: (score: number) => void;
  gameScore: GameScore;
  gameId: number;
}

interface FloatingScore {
  id: number;
  points: number;
  x: number;
  y: number;
}

export const WordWormGameModal: React.FC<WordWormGameModalProps> = ({
  isOpen = true,
  onClose,
  onHighScore,
  gameScore,
  gameId,
}) => {
  const [letters, setLetters] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isHighScore, setIsHighScore] = useState(false);
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([]);
  const [usedWords, setUsedWords] = useState<string[]>([]);
  const [localGameScore, setLocalGameScore] = useState(gameScore);
  const nextScoreId = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTouchDevice = useRef(false);
  const hasCalledHighScore = useRef(false);

  // Initialize local high score from cookie
  useEffect(() => {
    if (!gameScore?.user_high_score?.username) {
      const cookie = document.cookie
        .split('; ')
        .find(row => row.startsWith(`high_score_${gameId}=`));
      const localHighScore = cookie ? parseInt(cookie.split('=')[1], 10) : 0;
      
      setLocalGameScore({
        ...gameScore,
        user_high_score: {
          score: localHighScore
        }
      });
    } else {
      setLocalGameScore(gameScore);
    }
  }, [gameScore, gameId]);

  useEffect(() => {
    isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  useEffect(() => {
    if (!isTouchDevice.current && !gameOver) {
      inputRef.current?.focus();
    }
  }, [gameOver]);

  useEffect(() => {
    audioRef.current = new Audio(beepSound);
  }, []);

  const playBeep = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.warn('Audio playback failed:', error);
      });
    }
  }, []);

  const fourLetterWords = Object.keys(wordList as WordListType).filter(
    (word) => (wordList as WordListType)[word] === 4,
  );

  const addFloatingScore = useCallback(
    (points: number, x: number, y: number) => {
      const id = nextScoreId.current++;
      setFloatingScores((prev) => [...prev, { id, points, x, y }]);
      setTimeout(() => {
        setFloatingScores((prev) => prev.filter((score) => score.id !== id));
      }, 1000);
    },
    [],
  );

  const reset = () => {
    setLetters([]);
    setScore(0);
    setGameOver(false);
    setIsHighScore(false);
    setUsedWords([]);
    hasCalledHighScore.current = false;

    // Clear input value manually to allow typing again
    if (inputRef.current) {
      inputRef.current.value = '';
      if (!isTouchDevice.current) {
        inputRef.current.focus();
      }
    }

    if (!isTouchDevice.current) {
      inputRef.current?.focus();
    }
  };

  const handleDisplayClick = useCallback(() => {
    if (gameOver) return;
    inputRef.current?.focus();
  }, [gameOver]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (gameOver) return;

    const value = e.target.value.toUpperCase();
    const lettersOnly = value.replace(/[^A-Z]/g, '');

    if (usedWords.length === 0) {
      // Before first word is completed, allow backspace
      if (lettersOnly.length <= 3) {
        setLetters(lettersOnly.split(''));
        return;
      }
    } else {
      // After first word, disallow backspace (only allow appending one new character)
      if (lettersOnly.length < letters.length) {
        e.target.value = letters.join('');
        return;
      }
    }

    if (lettersOnly.length > 4) {
      e.target.value = letters.join('');
      return;
    }

    if (lettersOnly.length === 4) {
      const word = lettersOnly.toLowerCase();

      if (fourLetterWords.includes(word)) {
        if (usedWords.includes(word)) {
          // Check for high score before setting game over
          if (onHighScore && localGameScore) {
            const currentHighScore = localGameScore.user_high_score?.score ?? 0;
            if (score + 1 > currentHighScore) {
              setIsHighScore(true);
              onHighScore(score + 1);
            }
          }
          setGameOver(true);
          return;
        }

        setScore((s) => s + 1);
        setUsedWords((prev) => [...prev, word]);

        const scoreElement = document.querySelector('.flgm-score');
        if (scoreElement) {
          const rect = scoreElement.getBoundingClientRect();
          addFloatingScore(1, rect.left, rect.top);
        }

        playBeep();

        const newLetters = lettersOnly.slice(1).split('');
        setLetters(newLetters);
        e.target.value = newLetters.join('');
      } else {
        // Check for high score before setting game over
        if (onHighScore && localGameScore) {
          const currentHighScore = localGameScore.user_high_score?.score ?? 0;
          if (score > currentHighScore) {
            setIsHighScore(true);
            onHighScore(score);
          }
        }
        setGameOver(true);
        inputRef.current?.blur();
      }
    } else {
      setLetters(lettersOnly.split(''));
    }
  }, [gameOver, fourLetterWords, usedWords, addFloatingScore, playBeep, letters, score, onHighScore, localGameScore]);

  // Add debug logging to track gameScore
  useEffect(() => {
    console.log('gameScore updated:', gameScore);
  }, [gameScore]);

  // Update the high score effect to only call once per game session
  useEffect(() => {
    if (!gameOver || !onHighScore || !gameScore || hasCalledHighScore.current) {
      return;
    }

    console.log('Checking high score:', {
      currentScore: score,
      currentHighScore: gameScore.user_high_score?.score ?? 0,
      gameScore
    });

    const currentHighScore = gameScore.user_high_score?.score ?? 0;
    if (score > currentHighScore) {
      hasCalledHighScore.current = true;
      onHighScore(score);
    }
  }, [gameOver, score, onHighScore, gameScore]);

  if (!isOpen) return null;

  return (
    <div className="game-overlay">
      <div className="game-modal word-snake">
        <button className="game-close" onClick={onClose || (() => {})}>
          ×
        </button>
        <div className="row">
          <h2>Word Worm</h2>
          <IconButton icon={faSyncAlt} onClick={reset} />
        </div>
        
        <div className="flgm-score">Score: {score}</div>

        {floatingScores.map(({ id, points, x, y }) => (
          <div
            key={id}
            className={`floating-score ${points < 0 ? 'negative' : ''}`}
            style={{ left: `${x}px`, top: `${y}px` }}
          >
            {points > 0 ? '+' : ''}
            {points}
          </div>
        ))}

        <div className="flgm-display" onClick={handleDisplayClick} role="button" tabIndex={0}>
          <input
            ref={inputRef}
            type="text"
            className="flgm-input"
            onChange={handleInputChange}
            maxLength={4}
            autoCapitalize="characters"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            inputMode="text"
            disabled={gameOver}
          />
          {letters.map((ch, i) => (
            <span key={i}>{ch}</span>
          ))}
          {Array.from({ length: 4 - letters.length }).map((_, i) => (
            <span key={`pad-${i}`} className="flgm-blank">
              _
            </span>
          ))}
        </div>

        {gameOver ? (
          <GameOver
            score={score}
            gameScore={localGameScore}
            onPlayAgain={reset}
            isHighScore={isHighScore}
          />
        ) : (
          <p className="flgm-instructions">
            Type as many consecutive 4 letter words as you can. No backspaces after your first word.
            <br />
            Example: DARE - AREA - REAX x
          </p>
        )}
        <CollapsibleWordList words={usedWords} />
      </div>
    </div>
  );
};

export default WordWormGameModal;
